<script setup lang="ts">
// @ts-expect-error — layout composable is JS without type declarations
import { useLayout, applicaLayout, utenteHaPersonalizzato } from '@/layout/composables/layout'
import { computed, onMounted, watch } from 'vue'
import { updatePreset, updateSurfacePalette } from '@primeuix/themes'
import { apiClient } from '@/api/client'
import { primaryColorsPreset, surfacesPreset } from '@/layout/composables/temiPreset'
import AppBreadcrumb from './AppBreadcrumb.vue'
import AppConfig from './AppConfig.vue'
import AppFooter from './AppFooter.vue'
import AppRightMenu from './AppRightMenu.vue'
import AppSidebar from './AppSidebar.vue'
import AppTopbar from './AppTopbar.vue'
import { useMenuStore } from '@/stores/menu'
import { useDashboardStore } from '@/stores/dashboard'
import SystemNotifDialog from '@/components/SystemNotifDialog.vue'
import TransizionePopup from '@/components/TransizionePopup.vue'
import ScreenshotBar from '@/components/common/ScreenshotBar.vue'
import ChangePasswordDialog from '@/components/ChangePasswordDialog.vue'
import PaletteRicerca from '@/components/PaletteRicerca.vue'
import AzioniDinamiche from '@/components/common/AzioniDinamiche.vue'
import { usePersonalizzazione } from '@/composables/usePersonalizzazione'
import { useSezioniNascoste } from '@/composables/useSezioniNascoste'
import { useRoute, useRouter } from 'vue-router'
import { usePluginContextStore } from '@/stores/pluginContext'
import { useInactivityTimeout } from '@/composables/useInactivityTimeout'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'
import AppUtilityFab from '@/layout/AppUtilityFab.vue'
import NovitaBanner from '@/modules/novita/components/NovitaBanner.vue'
import { inviaSegnaliBrowser } from '@/composables/useSegnaliBrowser'

const { layoutConfig, layoutState, hideMobileMenu } = useLayout()

// ── Cambio password obbligatorio ────────────────────────────────────────────
// Il portale classico, quando l'amministratore imposta «Richiedi il cambio password al prossimo
// accesso», mostra il cambio forzato appena si entra. Qui il flag arriva da /auth/me
// (mustChangePassword) e non dal token: così vale anche dopo un reload, e anche se il reset
// avviene mentre la sessione è già aperta.
// ⚠️ Mai durante un accesso passpartout (assistenza che entra come l'utente): chi assiste non
// conosce la password attuale e non deve cambiarla a nome suo. Il flag resta sull'utente e il
// cambio gli verrà richiesto al suo primo accesso vero.
const cambioPasswordObbligatorio = computed(() =>
  auth.me?.mustChangePassword === true && auth.me?.passpartout !== true,
)
async function passwordCambiata(): Promise<void> {
  // Ricarica il profilo: il backend ha azzerato il flag, così il dialog si chiude da sé.
  await auth.fetchMe()
}
const menuStore = useMenuStore()
const dashStore = useDashboardStore()
const route = useRoute()
const idPagina = computed(() => route.meta.idPagina ?? '')
const { config: layoutPluginConfig } = usePersonalizzazione(idPagina)
useSezioniNascoste(layoutPluginConfig)
const pluginCtx = usePluginContextStore()

// Disconnessione automatica per inattività (sicurezza PC condivisi). Attiva solo qui,
// nell'area autenticata (AppLayout è montato solo da loggati). Default 2h, avviso 60s prima.
const auth = useAuthStore()
const router = useRouter()
const { t } = useI18n()
const { warningVisible, secondsLeft, keepAlive } = useInactivityTimeout({
  onTimeout: async () => {
    try { await auth.logout() } finally {
      router.push({ path: '/login', query: { reason: 'inactivity' } })
    }
  },
  // Allinea il timer client alla policy server-side (sessionIdleMinutes da /auth/me), con lo
  // stesso fallback usato dal composable se non ancora caricata (VITE_INACTIVITY_TIMEOUT_MIN o 120).
  timeoutMin: () => auth.me?.sessionIdleMinutes,
})

onMounted(async () => {
  // Inizializza dark mode dal valore persistito in dashStore (localStorage)
  layoutConfig.darkTheme = dashStore.isDark
  menuStore.loadMenu()
  menuStore.loadUserMenu()
  await vestiComeVuoleLAzienda()
})

// Segnali del browser per le sessioni automatiche (solo super-admin): uno per ogni persona che entra.
watch(() => auth.me?.userAccountId, (id) => {
  if (id != null) inviaSegnaliBrowser(String(id))
}, { immediate: true })

/**
 * Applica l'aspetto scelto dall'azienda a chi non ha mai personalizzato il proprio.
 *
 * <p>Le impostazioni di aspetto vivono nel browser: senza questo, ogni operatore al primo accesso
 * — e a ogni cambio di dispositivo — vedrebbe i colori di serie invece di quelli della sua
 * organizzazione. Chi si è scelto i propri colori non viene toccato: è un predefinito, non
 * un'imposizione.
 */
async function vestiComeVuoleLAzienda() {
  if (utenteHaPersonalizzato()) return
  try {
    const { data } = await apiClient.get('/api/v2/aziende/layout-default')
    if (!data) return
    applicaLayout(data)                       // senza persistere: l'utente resta «non personalizzato»
    applicaTemaPrimeVue(data)
  } catch { /* nessun default d'azienda, o non raggiungibile: restano i valori di serie */ }
}

/**
 * Il colore primario e la tonalità delle superfici non bastano nello stato: vanno passati al
 * motore dei temi, altrimenti il valore cambia ma la pagina resta com'era.
 */
function applicaTemaPrimeVue(valori: { primary?: string; surface?: string | null; darkTheme?: boolean }) {
  if (valori.primary) {
    const p = primaryColorsPreset.find((c) => c.name === valori.primary)
    if (p) updatePreset({ semantic: { primary: p.palette } })
  }
  if (valori.surface) {
    const sp = surfacesPreset.find((x) => x.name === valori.surface)
    if (sp) updateSurfacePalette(sp.palette)
  }
  if (valori.darkTheme != null) {
    document.documentElement.classList.toggle('app-dark', valori.darkTheme)
    if (dashStore.isDark !== valori.darkTheme) dashStore.toggleDark()
  }
}

// Sync bidirezionale: AppConfig (Ultima) ↔ dashStore (persistenza localStorage)
watch(() => layoutConfig.darkTheme, (val: boolean) => {
  if (dashStore.isDark !== val) dashStore.toggleDark()
})
watch(() => dashStore.isDark, (val: boolean) => {
  layoutConfig.darkTheme = val
})

const containerClass = computed(() => {
  const styleClass: Record<string, boolean> = {
    'layout-overlay': layoutConfig.menuMode === 'overlay',
    'layout-static': layoutConfig.menuMode === 'static',
    'layout-slim': layoutConfig.menuMode === 'slim',
    'layout-slim-plus': layoutConfig.menuMode === 'slim-plus',
    'layout-horizontal': layoutConfig.menuMode === 'horizontal',
    'layout-reveal': layoutConfig.menuMode === 'reveal',
    'layout-drawer': layoutConfig.menuMode === 'drawer',
    'layout-sidebar-dark': layoutConfig.colorScheme === 'dark',
    'layout-menu-profile-active': layoutState.rightMenuActive,
    'layout-topbar-menu-active': layoutState.topbarMenuActive,
    'layout-overlay-active': layoutState.overlayMenuActive,
    'layout-mobile-active': layoutState.mobileMenuActive,
    'layout-static-inactive': layoutState.staticMenuInactive,
    'layout-sidebar-expanded': layoutState.sidebarExpanded,
    'layout-sidebar-anchored': layoutState.anchored,
    'app-dark': layoutConfig.darkTheme,
  }

  styleClass['layout-topbar-' + layoutConfig.topbarTheme] = true
  styleClass['layout-menu-' + layoutConfig.menuTheme] = true
  styleClass['layout-menu-profile-' + layoutConfig.menuProfilePosition] = true

  return styleClass
})
</script>

<template>
  <div
    class="layout-wrapper"
    :class="containerClass"
  >
    <AppTopbar />
    <AppRightMenu />
    <AppSidebar />
    <div class="layout-content-wrapper">
      <AppBreadcrumb />
      <div class="layout-content">
        <!-- «Novità» della piattaforma: una card alla volta, vuota con l'interruttore generale spento. -->
        <NovitaBanner />
        <router-view />
        <AzioniDinamiche
          :config="layoutPluginConfig"
          posizione="BOTTOM_BAR"
          :entita-id="pluginCtx.entitaId"
          :entita-tipo="pluginCtx.entitaTipo"
          :contesto="pluginCtx.contesto"
          class="mt-4 px-4 pb-2"
        />
      </div>
      <AppFooter />
    </div>
    <AppConfig />
    <Toast />
    <SystemNotifDialog />
    <TransizionePopup />
    <PaletteRicerca />
    <!-- Barra della modalità cattura schermate (visibile solo quando è attiva): globale,
         così si può navigare tutto il portale continuando a scattare. -->
    <!-- Utilità trasversali (Strumenti PDF, richiesta assistenza): raggiungibili da ogni pagina
         senza occupare spazio né aggiungere una nona icona in topbar. -->
    <AppUtilityFab />
    <ScreenshotBar />
    <!-- Cambio password obbligatorio: dialog non chiudibile finché la password non è cambiata -->
    <ChangePasswordDialog
      :visible="cambioPasswordObbligatorio"
      forzato
      @success="passwordCambiata"
    />
    <Dialog
      v-model:visible="warningVisible"
      modal
      :closable="false"
      :draggable="false"
      :header="t('inactivity.title', 'Sessione in scadenza')"
      :style="{ width: '420px' }"
    >
      <p class="m-0">
        {{ t('inactivity.message', 'Per inattività verrai disconnesso tra') }}
        <strong>{{ secondsLeft }}s</strong>.
      </p>
      <template #footer>
        <Button
          :label="t('inactivity.keepAlive', 'Resta connesso')"
          icon="pi pi-check"
          @click="keepAlive"
        />
      </template>
    </Dialog>
    <div
      class="layout-mask"
      @click="hideMobileMenu"
    />
  </div>
</template>
